/**
 * Lighting Component
 * Provides scene lighting setup.
 *
 * Props:
 *   positionRef       — ref to [x, y, z] array; updated each frame by SunRotation
 *   shadowTargetRef   — optional ref to [x, y, z] array; when provided, the directional
 *                       light target and shadow frustum are centred on that position so
 *                       focused models receive correct self-shadowing.
 *                       When null/undefined the target stays at world origin (Earth view).
 *   shadowFrustumSize — half-size of the shadow camera frustum (default 5)
 *   shadowNear        — shadow camera near plane (default 45)
 *   shadowFar         — shadow camera far plane (default 55)
 *   shadowsEnabled    — directional light casts shadows (default true)
 *   shadowMapSize     — shadow map resolution (default 2048; 1024 on mobile portrait)
 *   ambientIntensity  — intensity of the ambient light (default 0.1)
 *   ambientColor      — color of the ambient light as hex string (default "#ffffff")
 *   showShadowHelper  — renders a CameraHelper visualising the shadow frustum (dev only)
 */
import { MeshBasicNodeMaterial, SphereGeometry } from 'three/webgpu'
import { vec3 } from 'three/tsl'
import { useMemo, useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Vector3, CameraHelper } from 'three'

const _targetPos = new Vector3()

export function Lighting({ positionRef, shadowTargetRef = null, shadowFrustumSize = 5, shadowNear = 45, shadowFar = 55, shadowBias = -0.001, ambientIntensity = 0.1, ambientColor = '#ffffff', showShadowHelper = false, shadowsEnabled = true, shadowMapSize = 2048 }) {
  const dirLightRef = useRef()
  const sunMeshRef  = useRef()
  const helperRef       = useRef()
  const { scene }       = useThree()

  const sunMaterial = useMemo(() => {
    const material = new MeshBasicNodeMaterial()
    material.colorNode    = vec3(5, 5, 1)
    material.emissiveNode = vec3(20, 10, 10)
    return material
  }, [])

  const sunGeometry = useMemo(() => new SphereGeometry(1, 8, 8), [])

  useEffect(() => {
    return () => {
      sunGeometry.dispose()
      sunMaterial.dispose()
    }
  }, [sunGeometry, sunMaterial])

  // shadowsEnabled/shadowMapSize are deps because the light remounts (key change)
  // when they change — the NEW light's target must be re-added to the scene.
  useEffect(() => {
    if (!dirLightRef.current) return
    const target = dirLightRef.current.target
    scene.add(target)
    return () => {
      scene.remove(target)
    }
  }, [scene, shadowsEnabled, shadowMapSize])

  useEffect(() => {
    if (!dirLightRef.current) return
    if (showShadowHelper) {
      const helper = new CameraHelper(dirLightRef.current.shadow.camera)
      helperRef.current = helper
      scene.add(helper)
    }
    return () => {
      if (helperRef.current) {
        scene.remove(helperRef.current)
        helperRef.current.dispose()
        helperRef.current = null
      }
    }
  }, [showShadowHelper, scene])

  // Cache last-applied frustum values so updateProjectionMatrix is only called on change
  const lastFrustumRef = useRef(null)
  const lastNearRef    = useRef(null)
  const lastFarRef     = useRef(null)

  useFrame(() => {
    if (!positionRef?.current || !dirLightRef.current) return
    const [x, y, z] = positionRef.current

    dirLightRef.current.position.set(x, y, z)
    sunMeshRef.current?.position.set(x, y, z)

    if (shadowTargetRef?.current) {
      const [tx, ty, tz] = shadowTargetRef.current
      _targetPos.set(tx, ty, tz)
    } else {
      _targetPos.set(0, 0, 0)
    }

    dirLightRef.current.target.position.copy(_targetPos)
    dirLightRef.current.target.updateMatrixWorld()

    // Only rebuild shadow camera projection when values actually change
    if (
      lastFrustumRef.current !== shadowFrustumSize ||
      lastNearRef.current    !== shadowNear ||
      lastFarRef.current     !== shadowFar
    ) {
      const cam = dirLightRef.current.shadow.camera
      cam.left   = -shadowFrustumSize
      cam.right  =  shadowFrustumSize
      cam.top    =  shadowFrustumSize
      cam.bottom = -shadowFrustumSize
      cam.near   =  shadowNear
      cam.far    =  shadowFar
      cam.updateProjectionMatrix()
      lastFrustumRef.current = shadowFrustumSize
      lastNearRef.current    = shadowNear
      lastFarRef.current     = shadowFar
    }

    helperRef.current?.update()
  })

  return (
    <>
      <ambientLight intensity={ambientIntensity} color={ambientColor} />
      {/* key remount on shadow toggle/resize — mutating castShadow/mapSize at
          runtime does not reliably re-hash lighting nodes or dispose the old
          shadow map on the r183 WebGPU node backend; remounting one light is
          cheap and R3F disposes the old shadow map cleanly. */}
      <directionalLight
        key={`dirlight-${shadowsEnabled}-${shadowMapSize}`}
        ref={dirLightRef}
        intensity={2.5}
        castShadow={shadowsEnabled}
        shadow-mapSize-width={shadowMapSize}
        shadow-mapSize-height={shadowMapSize}
        shadow-camera-left={-shadowFrustumSize}
        shadow-camera-right={shadowFrustumSize}
        shadow-camera-top={shadowFrustumSize}
        shadow-camera-bottom={-shadowFrustumSize}
        shadow-camera-near={shadowNear}
        shadow-camera-far={shadowFar}
        shadow-bias={shadowBias}
      />
      <mesh ref={sunMeshRef} material={sunMaterial} geometry={sunGeometry} />
    </>
  )
}

export default Lighting
